(() => {
  if (window.NordwegLegacyHomeFeatureController) {
    window.NordwegLegacyHomeFeatureController.initializeSections();
    return;
  }

  const sectionSelector = '.legacy-home-feature[data-section-id]';
  const instances = new Map();
  const getSectionId = (section) => section?.dataset?.sectionId;

  const cleanup = (sectionId) => {
    const instance = instances.get(sectionId);
    if (!instance) return;
    instance.dispose();
    instances.delete(sectionId);
  };

  const initialize = (section) => {
    const sectionId = getSectionId(section);
    if (!sectionId) return;
    cleanup(sectionId);

    let disposed = false;
    let proximityObserver = null;
    let visibilityObserver = null;
    const imagePromiseCache = new Map();
    const imagePreloads = [];
    const requestSequences = new WeakMap();
    const eventListeners = [];
    let releaseVideo = () => {};

    const disconnectVideoObservers = () => {
      proximityObserver?.disconnect();
      visibilityObserver?.disconnect();
      proximityObserver = null;
      visibilityObserver = null;
    };

    const stateFromSwatch = (swatch, fallbackSizes = '') => ({
      src: swatch.dataset.colorImage,
      srcset: swatch.dataset.colorSrcset,
      sizes: swatch.dataset.colorSizes || fallbackSizes,
      alt: swatch.dataset.colorAlt,
      width: Number.parseInt(swatch.dataset.colorWidth, 10),
      height: Number.parseInt(swatch.dataset.colorHeight, 10),
      url: swatch.dataset.colorUrl,
      status: swatch.dataset.colorStatus,
      loading: swatch.dataset.colorLoading,
      error: swatch.dataset.colorError,
      variantId: swatch.dataset.variantId,
    });

    const preloadImage = (state) => {
      const preloadKey = state.srcset || state.src;
      if (disposed || !state.src) return Promise.reject(new Error('Missing variant image'));
      if (imagePromiseCache.has(preloadKey)) return imagePromiseCache.get(preloadKey);

      const image = new Image();
      image.decoding = 'async';
      if (state.sizes) image.sizes = state.sizes;
      if (state.srcset) image.srcset = state.srcset;

      const promise = new Promise((resolve, reject) => {
        let settled = false;
        const settle = (callback, value) => {
          if (settled) return;
          settled = true;
          callback(value);
        };
        const finish = () => {
          const decode = typeof image.decode === 'function' ? image.decode() : Promise.resolve();
          Promise.resolve(decode)
            .catch(() => {})
            .then(() => settle(resolve, image));
        };
        image.onload = finish;
        image.onerror = () => settle(reject, new Error('Variant image failed to load'));
        image.src = state.src;
        if (image.complete && image.naturalWidth > 0) Promise.resolve().then(finish);
      }).catch((error) => {
        imagePromiseCache.delete(preloadKey);
        throw error;
      });

      imagePromiseCache.set(preloadKey, promise);
      imagePreloads.push(image);
      return promise;
    };

    const listen = (element, type, listener) => {
      element.addEventListener(type, listener);
      eventListeners.push([element, type, listener]);
    };

    const swatchFromEvent = (event) => {
      const target = event.target?.closest?.('[data-legacy-swatch]');
      return target && section.contains(target) ? target : null;
    };

    const warmSwatch = (swatch) => {
      const card = swatch?.closest('.legacy-home-feature__product');
      const image = card?.querySelector('.legacy-home-feature__product-media img');
      if (!image) return;
      preloadImage(stateFromSwatch(swatch, image.sizes)).catch(() => {});
    };

    const selectSwatch = async (swatch) => {
      const card = swatch?.closest('.legacy-home-feature__product');
      const image = card?.querySelector('.legacy-home-feature__product-media img');
      if (!card || !image) return;

      const requestSequence = (requestSequences.get(card) || 0) + 1;
      requestSequences.set(card, requestSequence);
      const state = stateFromSwatch(swatch, image.sizes);
      const status = card.querySelector('[data-legacy-swatch-status]');
      if (swatch.getAttribute('aria-pressed') === 'true') {
        card.setAttribute('aria-busy', 'false');
        card.classList.remove('is-switching');
        if (status && state.status) status.textContent = state.status;
        return;
      }

      card.setAttribute('aria-busy', 'true');
      card.classList.add('is-switching');
      if (status && state.loading) status.textContent = state.loading;

      try {
        await preloadImage(state);
        if (disposed || requestSequences.get(card) !== requestSequence) return;

        if (state.sizes) image.sizes = state.sizes;
        if (state.srcset) image.srcset = state.srcset;
        image.src = state.src;
        if (state.alt) image.alt = state.alt;
        if (state.width > 0) image.width = state.width;
        if (state.height > 0) image.height = state.height;
        image.dataset.originalSrc = state.src;
        image.dataset.originalAlt = state.alt || '';

        card.querySelectorAll('[data-legacy-swatch]').forEach((item) => {
          const selected = item === swatch;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        card.querySelectorAll('[data-card-product-link]').forEach((link) => {
          if (state.url) link.href = state.url;
        });
        const price = card.querySelector('[data-card-price]');
        const priceState = card.querySelector(`[data-legacy-price-state="${state.variantId}"]`);
        if (price && priceState) price.replaceChildren(priceState.content.cloneNode(true));
        if (state.variantId) card.dataset.selectedVariantId = state.variantId;
        if (status && state.status) status.textContent = state.status;
      } catch (_error) {
        if (disposed || requestSequences.get(card) !== requestSequence) return;
        if (status && state.error) status.textContent = state.error;
      } finally {
        if (!disposed && requestSequences.get(card) === requestSequence) {
          card.setAttribute('aria-busy', 'false');
          card.classList.remove('is-switching');
        }
      }
    };

    listen(section, 'pointerover', (event) => {
      const swatch = swatchFromEvent(event);
      if (!swatch || swatch.contains(event.relatedTarget)) return;
      warmSwatch(swatch);
      if (section.dataset.swatchPreview === 'true') selectSwatch(swatch);
    });
    listen(section, 'focusin', (event) => {
      const swatch = swatchFromEvent(event);
      if (swatch) warmSwatch(swatch);
    });
    listen(section, 'focusout', (event) => {
      const swatch = swatchFromEvent(event);
      swatch?.classList.remove('is-pointer-focus');
    });
    listen(section, 'pointerdown', (event) => {
      const swatch = swatchFromEvent(event);
      if (!swatch) return;
      swatch.classList.add('is-pointer-focus');
      warmSwatch(swatch);
    });
    listen(section, 'click', (event) => {
      const swatch = swatchFromEvent(event);
      if (!swatch) return;
      event.preventDefault();
      if (event.detail > 0) swatch.classList.add('is-pointer-focus');
      selectSwatch(swatch);
    });

    const video = section.querySelector('[data-legacy-home-feature-video]');
    const videoToggle = section.querySelector('[data-legacy-home-feature-video-toggle]');
    const lookbookOpener = section.querySelector('[data-legacy-home-feature-lookbook-open]');
    const lookbookDialog = section.querySelector('[data-legacy-home-feature-lookbook]');
    const lookbookPane = section.querySelector('[data-legacy-home-feature-lookbook-pane]');
    const lookbookStage = section.querySelector('[data-legacy-home-feature-lookbook-stage]');
    const lookbookFigure = section.querySelector('[data-legacy-home-feature-lookbook-figure]');
    const lookbookClose = section.querySelector('[data-legacy-home-feature-lookbook-close]');
    const videoContainer = video?.closest('.legacy-home-feature__video');
    const sources = video ? Array.from(video.querySelectorAll('source[data-src]')) : [];
    const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
    const videoProximityMargin = 300;
    const prefersReducedMotion = () => motionQuery?.matches;

    if (video && sources.length > 0) {
      let videoIsNear = false;
      let videoIsVisible = false;
      let manuallyPaused = false;
      let explicitPlayback = false;
      let lookbookOpen = false;
      let returnFocusElement = null;
      const inlineVideoParent = video.parentNode;
      const inlineVideoNextSibling = video.nextSibling;
      const sourcesLoaded = () => sources.every((source) => source.hasAttribute('src'));
      const playLabel = videoToggle?.dataset.playLabel || videoToggle?.getAttribute?.('aria-label') || '';
      const pauseLabel = videoToggle?.dataset.pauseLabel || playLabel;

      const syncVideoControl = () => {
        if (!videoToggle) return;
        const isPlaying = !video.paused && !video.ended;
        videoToggle.dataset.playing = isPlaying ? 'true' : 'false';
        videoToggle.setAttribute('aria-label', isPlaying ? pauseLabel : playLabel);
      };

      listen(video, 'playing', () => {
        if (!lookbookOpen) videoContainer?.classList.add('is-video-playing');
        syncVideoControl();
      });
      listen(video, 'pause', syncVideoControl);
      listen(video, 'ended', syncVideoControl);
      listen(video, 'error', () => {
        explicitPlayback = false;
        videoContainer?.classList.remove('is-video-playing');
        syncVideoControl();
      });

      const unloadVideo = () => {
        explicitPlayback = false;
        videoContainer?.classList.remove('is-video-playing');
        if (!video.paused) video.pause();
        if (!sourcesLoaded() && !video.hasAttribute?.('src')) {
          video.preload = 'none';
          return;
        }
        video.removeAttribute('src');
        sources.forEach((source) => source.removeAttribute('src'));
        video.preload = 'none';
        video.load();
      };

      const loadVideo = (allowReducedMotion = false) => {
        if (disposed || (prefersReducedMotion() && !allowReducedMotion)) return false;
        if (!sourcesLoaded()) {
          video.preload = 'auto';
          sources.forEach((source) => {
            if (!source.hasAttribute('src') && source.dataset.src) source.src = source.dataset.src;
          });
          video.load();
        }
        return true;
      };

      const playVideo = (isExplicit = false) => {
        if (
          disposed
          || lookbookOpen
          || manuallyPaused
          || (prefersReducedMotion() && !isExplicit)
          || (!isExplicit && (!videoIsNear || !videoIsVisible))
          || !loadVideo(isExplicit)
          || !video.paused
        ) return;
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {
            explicitPlayback = false;
            syncVideoControl();
          });
        }
      };

      const syncVideo = () => {
        if (lookbookOpen) {
          if (!video.paused) video.pause();
          return;
        }
        if (disposed || !videoIsNear || (prefersReducedMotion() && !explicitPlayback)) {
          unloadVideo();
          return;
        }
        if (manuallyPaused) {
          if (!video.paused) video.pause();
          return;
        }
        loadVideo(explicitPlayback);
        if (videoIsVisible) {
          playVideo(explicitPlayback);
        } else {
          explicitPlayback = false;
          if (!video.paused) video.pause();
          if (prefersReducedMotion()) unloadVideo();
        }
      };

      const restoreInlineVideo = () => {
        if (video.parentNode !== inlineVideoParent) {
          if (inlineVideoNextSibling?.parentNode === inlineVideoParent) {
            inlineVideoParent?.insertBefore(video, inlineVideoNextSibling);
          } else {
            inlineVideoParent?.appendChild(video);
          }
        }
        video.controls = false;
        video.loop = true;
        video.muted = true;
        video.setAttribute('aria-hidden', 'true');
        video.removeAttribute('aria-label');
        video.removeAttribute('controlslist');
        video.removeAttribute('disableremoteplayback');
        video.removeAttribute('poster');
        video.classList.remove('shoppable-media-lookbook__media');
      };

      const closeLookbook = (restoreFocus = true) => {
        if (!lookbookOpen && !lookbookDialog?.open) return;
        lookbookOpen = false;
        if (!video.paused) video.pause();
        restoreInlineVideo();
        videoContainer?.classList.remove('is-video-playing');
        if (lookbookDialog?.open) lookbookDialog.close();
        document.documentElement.classList.remove('shoppable-media-lookbook-open');
        document.body?.classList.remove('shoppable-media-lookbook-open');
        if (restoreFocus && returnFocusElement?.isConnected) returnFocusElement.focus();
        returnFocusElement = null;
        syncVideoControl();
        if (!disposed) syncVideo();
      };

      const openLookbook = (event) => {
        if (
          disposed
          || lookbookOpen
          || !lookbookOpener
          || !lookbookDialog
          || !lookbookFigure
          || typeof lookbookDialog.showModal !== 'function'
        ) return;

        event.preventDefault();
        if (!loadVideo(true)) return;
        returnFocusElement = lookbookOpener;
        lookbookOpen = true;
        if (!video.paused) video.pause();
        videoContainer?.classList.remove('is-video-playing');
        video.controls = true;
        video.loop = false;
        video.setAttribute('aria-hidden', 'false');
        video.setAttribute('aria-label', video.dataset.lookbookLabel || lookbookOpener.getAttribute?.('aria-label') || '');
        video.setAttribute('controlslist', 'nodownload noremoteplayback nofullscreen');
        video.setAttribute('disableremoteplayback', '');
        if (video.dataset.lookbookPosterUrl) video.setAttribute('poster', video.dataset.lookbookPosterUrl);
        video.classList.add('shoppable-media-lookbook__media');
        lookbookFigure.appendChild(video);
        lookbookDialog.showModal();
        document.documentElement.classList.add('shoppable-media-lookbook-open');
        document.body?.classList.add('shoppable-media-lookbook-open');
        lookbookClose?.focus();
        if (!prefersReducedMotion()) {
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        }
        syncVideoControl();
      };

      if (lookbookOpener && lookbookDialog && lookbookFigure && lookbookClose) {
        listen(lookbookOpener, 'click', openLookbook);
        listen(lookbookClose, 'click', () => closeLookbook());
        listen(lookbookDialog, 'cancel', (event) => {
          event.preventDefault();
          closeLookbook();
        });
        listen(lookbookDialog, 'click', (event) => {
          if (
            event.target === lookbookDialog
            || event.target === lookbookPane
            || event.target === lookbookStage
            || event.target === lookbookFigure
          ) closeLookbook();
        });
      }

      releaseVideo = () => {
        closeLookbook(false);
        unloadVideo();
      };

      const observeVideo = () => {
        if (disposed) return;
        disconnectVideoObservers();
        if ('IntersectionObserver' in window) {
          proximityObserver = new IntersectionObserver((entries) => {
            if (disposed) return;
            videoIsNear = entries.some((entry) => entry.isIntersecting);
            syncVideo();
          }, { rootMargin: `${videoProximityMargin}px 0px`, threshold: 0 });
          visibilityObserver = new IntersectionObserver((entries) => {
            if (disposed) return;
            videoIsVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio > 0.08);
            syncVideo();
          }, { rootMargin: '0px 0px', threshold: [0, 0.08, 0.25] });
          proximityObserver.observe(video);
          visibilityObserver.observe(video);
        } else {
          videoIsNear = true;
          videoIsVisible = true;
          syncVideo();
        }
      };

      if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', observeVideo);
      else observeVideo();

      if (videoToggle) {
        listen(videoToggle, 'click', (event) => {
          event.preventDefault();
          if (video.paused || video.ended) {
            manuallyPaused = false;
            explicitPlayback = true;
            if (video.ended) video.currentTime = 0;
            playVideo(true);
          } else {
            manuallyPaused = true;
            explicitPlayback = false;
            video.pause();
          }
          syncVideoControl();
        });
      }

      if (motionQuery) {
        listen(motionQuery, 'change', (event) => {
          if (event.matches) {
            if (lookbookOpen) {
              if (!video.paused) video.pause();
            } else {
              unloadVideo();
            }
          } else if (!lookbookOpen) {
            syncVideo();
          }
        });
      }

      syncVideoControl();
    }

    instances.set(sectionId, {
      dispose: () => {
        disposed = true;
        disconnectVideoObservers();
        releaseVideo();
        eventListeners.forEach(([element, type, listener]) => element.removeEventListener(type, listener));
        videoContainer?.classList.remove('is-video-playing');
        section.querySelectorAll('.legacy-home-feature__product').forEach((card) => {
          card.setAttribute('aria-busy', 'false');
          card.classList.remove('is-switching');
          const status = card.querySelector('[data-legacy-swatch-status]');
          if (status) status.textContent = '';
        });
        imagePromiseCache.clear();
        imagePreloads.length = 0;
      },
    });
  };

  const initializeSections = (root = document) => {
    if (root.matches?.(sectionSelector)) initialize(root);
    root.querySelectorAll?.(sectionSelector).forEach(initialize);
  };

  window.NordwegLegacyHomeFeatureController = { initialize, initializeSections, cleanup };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeSections(), { once: true });
  } else {
    initializeSections();
  }

  document.addEventListener('shopify:section:load', (event) => initializeSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(sectionSelector)
      ? event.target
      : event.target.querySelector?.(sectionSelector);
    const sectionId = getSectionId(section) || event.detail?.sectionId;
    if (sectionId) cleanup(sectionId);
  });
})();
